export const INSURANCE_COVER = {
  STANDARD: "STANDARD",
  EXTENDED: "EXTENDED",
} as const;

export type InsuranceCover =
  (typeof INSURANCE_COVER)[keyof typeof INSURANCE_COVER];

export interface InsuranceCoverContent {
  label: string;
  protectionText: string;
  mmvSummary: {
    highlight: string;
    body: string;
  };
  coverageItems: Array<{
    duration: string;
    type: string;
  }>;
  compareRows: Array<{
    label: string;
    standard: string;
    extended: string;
  }>;
  defaultPlanNote: string;
  modelHeadeing: string;
  modalItems: Array<{
    image: string;
    boldTitle?: string;
    title: string;
    description: string;
  }>;
}

export interface PricingInsuranceCoverOption {
  cover_type: InsuranceCover;
  label?: string;
  protection_text?: string;
  plan_id?: string;
  selected?: boolean;
  available?: boolean;
  market_price?: number;
  on_road_price?: number;
  acko_drive_price?: number;
  acko_drive_savings?: number;
  pricing_components?: Record<string, number | string>;
  discount_components?: Record<string, number | string>;
}

export const INSURANCE_COVER_CONTENT: Record<
  InsuranceCover,
  InsuranceCoverContent
> = {
  STANDARD: {
    label: "Standard cover",
    protectionText: "1 year protection",
    mmvSummary: {
      highlight: "1 year zero dep + 3 year third party cover.",
      body: "You'll need to renew after 1st year, and premiums typically go up each renewal.",
    },
    coverageItems: [
      {
        duration: "3 Year",
        type: "Third Party Cover",
      },
      {
        duration: "1 Year",
        type: "Zero depreciation cover",
      },
    ],
    compareRows: [
      {
        label: "Third-party liability (TP)",
        standard: "3",
        extended: "3",
      },
      {
        label: "Own damage (OD)",
        standard: "1",
        extended: "3",
      },
      {
        label: "Zero depreciation",
        standard: "1",
        extended: "3",
      },
      {
        label: "Extra car protection",
        standard: "1",
        extended: "3",
      },
      {
        label: "15 lakh personal accident cover",
        standard: "1",
        extended: "3",
      },
    ],
    defaultPlanNote:
      "Your default plan will be Standard plan, you can upgrade to extended cover during the quote process.",
    modelHeadeing: "Your car will be insured for 1 year - Standard cover",
    modalItems: [
      {
        image: "/icons/Caraccident.svg",
        title: "Zero depreciation (Bumper to bumper) Cover for 1 year",
        description:
          "Covers theft and damage caused to your car by accidents, natural calamities and fire. It also pays 100% of the cost of replaced parts during a claim.",
      },
      {
        image: "/icons/thirdPartyCover.svg",
        title: "Third Party Cover for 3 year",
        description:
          "Covers damage caused by your car to others and their property.",
      },
      {
        image: "/icons/tow_truck.webp",
        title: "Extra car protection for 1 year",
        description:
          "Provides 24x7 car breakdown assistance. Covers the cost of key repair/replacement up to ₹7,000. It also pays accommodation expenses up to ₹6,500 during outstation repairs.",
      },
      {
        image: "/icons/personal_accident.webp",
        title: "15 lakh personal accident cover for 1 year",
        description:
          "Pays up to ₹15 lakh for accidental death or injury of the car owner.",
      },
    ],
  },
  EXTENDED: {
    label: "Extended cover",
    protectionText: "3 year protection",
    mmvSummary: {
      highlight: "3 year zero dep + 3 year third party cover.",
      body: "Full claim value, no deductions, for 3 years.",
    },
    coverageItems: [
      {
        duration: "3 Year",
        type: "Third Party Cover",
      },
      {
        duration: "3 Year",
        type: "Zero depreciation cover",
      },
    ],
    compareRows: [
      {
        label: "Third-party liability (TP)",
        standard: "3",
        extended: "3",
      },
      {
        label: "Own damage (OD)",
        standard: "1",
        extended: "3",
      },
      {
        label: "Zero depreciation",
        standard: "1",
        extended: "3",
      },
      {
        label: "Extra car protection",
        standard: "1",
        extended: "3",
      },
      {
        label: "15 lakh personal accident cover",
        standard: "1",
        extended: "3",
      },
    ],
    defaultPlanNote:
      "Your default plan will be Standard plan, you can upgrade to extended cover during the quote process.",
    modelHeadeing: "Your car will be insured for 3 years - Extended cover",
    modalItems: [
      {
        image: "/icons/Caraccident.svg",
        title: "Zero depreciation (Bumper to bumper) Cover",
        description:
          "Covers theft and damage caused to your car by accidents, natural calamities and fire. It also pays 100% of the cost of replaced parts during a claim.",
      },
      {
        image: "/icons/thirdPartyCover.svg",
        title: "Third Party Cover",
        description:
          "Covers damage caused by your car to others and their property.",
      },
      {
        image: "/icons/tow_truck.webp",
        title: "Extra car protection",
        description:
          "Provides 24x7 car breakdown assistance. Covers the cost of key repair/replacement up to ₹7,000. It also pays accommodation expenses up to ₹6,500 during outstation repairs.",
      },
      {
        image: "/icons/personal_accident.webp",
        title: "15 lakh personal accident cover",
        description:
          "Pays up to ₹15 lakh for accidental death or injury of the car owner.",
      },
    ],
  },
};

export const getNormalizedInsuranceCover = (
  value?: string | null
): InsuranceCover => {
  if (value === INSURANCE_COVER.EXTENDED) {
    return INSURANCE_COVER.EXTENDED;
  }

  return INSURANCE_COVER.STANDARD;
};

export const getQuoteInsuranceCover = (quoteDetails?: {
  insurance_cover?: string | null;
  insurance_type?: string | null;
  insurance_cover_type?: string | null;
} | null): InsuranceCover => {
  return getNormalizedInsuranceCover(
    quoteDetails?.insurance_cover_type ||
      quoteDetails?.insurance_cover ||
      quoteDetails?.insurance_type || ""
  );
};

export type InsurancePlanSelectedValue = "standard" | "extended";

export const getInsurancePlanSelectedFromCover = (
  cover?: string | null
): InsurancePlanSelectedValue => {
  return getNormalizedInsuranceCover(cover) === INSURANCE_COVER.EXTENDED
    ? "extended"
    : "standard";
};

export const getInsurancePlanSelectedLabel = (quoteDetails?: {
  insurance_cover?: string | null;
  insurance_type?: string | null;
  insurance_cover_type?: string | null;
} | null): InsurancePlanSelectedValue => {
  return getInsurancePlanSelectedFromCover(getQuoteInsuranceCover(quoteDetails));
};

export const getInsuranceCoverContent = (
  value?: string | null
): InsuranceCoverContent => {
  return INSURANCE_COVER_CONTENT[getNormalizedInsuranceCover(value)];
};
